import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Input,
  Textarea,
  Label,
  Switch,
  MediaPreview,
  SearchableCombobox,
  cn,
} from "@nexus/ui";
import { uniqueVendorCapabilities, type VendorCapability } from "@nexus/contracts";
import {
  Plus,
  Trash2,
  Upload,
  X,
  ImageIcon,
  ListChecks,
  Package,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  adminVendorQueryOptions,
  useCreateAdminVendor,
  useReplaceAdminVendor,
  useSetVendorContactEmailApproval,
  useLinkPendingVendor,
  useEnablePartnerLogin,
  useUnlinkPartnerLogin,
} from "@/lib/queries";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  presignVendorLogoUpload,
  confirmVendorLogoUpload,
} from "@/features/marketplace/api/vendor-logo-upload.api";
import { putToSignedUrlWithProgress, stageMemoryPreflightUpload } from "@/features/preflight/api/uploads.api";
import {
  getVendorLogoMaxBytes,
  isAllowedVendorLogoType,
  VENDOR_LOGO_ACCEPT,
} from "@/features/marketplace/lib/vendor-logo-upload-constraints";
import { getVendorInitials } from "@/features/marketplace/lib/vendorInitials";
import { isPackageCadence } from "@/features/marketplace/lib/package-cadence";
import { TermsField } from "./TermsField";
import { QuestionEditor } from "./QuestionEditor";
import { PackageEditor } from "./PackageEditor";
import { CollapsibleSection } from "./CollapsibleSection";
import { FormNavRail } from "./FormNavRail";
import { PartnerOrgSection } from "./PartnerOrgSection";
import { SortableRow, SectionEmpty } from "./FormParts";
import { VendorCapabilitiesField } from "./VendorCapabilitiesField";
import {
  VENDOR_FORM_LIMITS,
  VENDOR_TEXT_LIMITS,
  createEmptyPackage,
  createEmptyQuestion,
  isContactEmailPersisted,
  resolveLegacyContactEmail,
  toEditablePackage,
  toEditableQuestion,
  validateVendorBasics,
  validateVendorContactEmails,
  validateVendorSubEntities,
  SECTION_ID,
  type EditablePackage,
  type EditableQuestion,
  type SectionKey,
  type VendorBasicsField,
  type VendorFieldErrors,
  type VendorContactEmailErrors,
} from "../lib/admin-vendor-form.utils";
import { findVerticalScrollParent, scrollSectionToStart } from "../lib/scroll-section-into-scroller";
import {
  CLEAN_SECTION_DIRTY,
  captureVendorFormDirtySnapshot,
  getDirtyVendorFormSections,
  type VendorFormDirtySnapshot,
} from "../lib/admin-vendor-form-dirty";

import type { AdminVendorRow } from "@/features/admin/marketplace/types/admin-marketplace.types";
import type {
  AdminVendorWritePayload,
  PendingVendorOnboardingContext,
} from "@/features/admin/marketplace/types/admin-marketplace.types";

interface AdminVendorFormProps {
  onClose: () => void;
  vendor?: AdminVendorRow;
  pendingContext?: PendingVendorOnboardingContext;
}

/** Validation order, used to focus the first invalid field on submit. */
const BASICS_FIELD_ORDER: VendorBasicsField[] = [
  "name",
  "slug",
  "websiteUrl",
  "termsMarkdown",
];

const BASICS_FIELD_INPUT_ID: Record<VendorBasicsField, string> = {
  name: "vf-name",
  slug: "vf-slug",
  websiteUrl: "vf-web",
  termsMarkdown: "vf-terms",
};

const CONTACT_EMAIL_COUNT_MAX = 10;

/** A contact address row while it is being edited; `id` is absent until saved. */
interface EditableContactEmail {
  uid: string;
  id?: string;
  email: string;
  approval: "APPROVED" | "PENDING";
  isPrimary: boolean;
  receivesRevisionRequested: boolean;
  receivesCommentUpdates: boolean;
  /**
   * The address as currently stored. Approval acts on the persisted row, so an
   * edited-but-unsaved field would approve a different address than the one on
   * screen — the control stays disabled until the two match again.
   */
  persistedEmail?: string;
}

function newContactEmailRow(): EditableContactEmail {
  return {
    uid: crypto.randomUUID(),
    email: "",
    approval: "PENDING",
    isPrimary: false,
    receivesRevisionRequested: true,
    receivesCommentUpdates: true,
  };
}

/**
 * Seeds the editor from whichever address model the vendor already has. A
 * vendor predating per-address approval carries only the single `contactEmail`,
 * which is shown as its (approved) primary so an editor sees one consistent list.
 */
function toEditableContactEmails(vendor: AdminVendorRow | undefined): EditableContactEmail[] {
  if (vendor?.contactEmails?.length) {
    return vendor.contactEmails.map((row) => ({
      uid: crypto.randomUUID(),
      id: row.id,
      email: row.email,
      approval: row.approval,
      isPrimary: row.isPrimary,
      receivesRevisionRequested: row.receivesRevisionRequested,
      receivesCommentUpdates: row.receivesCommentUpdates,
      persistedEmail: row.email,
    }));
  }
  if (vendor?.contactEmail) {
    return [
      {
        uid: crypto.randomUUID(),
        email: vendor.contactEmail,
        approval: vendor.contactEmailApproval,
        isPrimary: true,
        receivesRevisionRequested: true,
        receivesCommentUpdates: true,
        persistedEmail: vendor.contactEmail,
      },
    ];
  }
  return [newContactEmailRow()];
}

/**
 * Builds a dnd-kit drag-end handler that reorders a uid-keyed list in place.
 * Packages and questions share identical reorder logic, differing only by setter.
 */
function makeUidDragEndHandler<T extends { uid: string }>(
  setItems: React.Dispatch<React.SetStateAction<T[]>>,
) {
  return (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIndex = prev.findIndex((item) => item.uid === active.id);
      const newIndex = prev.findIndex((item) => item.uid === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };
}

/** Prisma Decimal serializes as string over JSON — normalize before submit. */
function normalizePackagePrice(price: number | string | null | undefined): number | null {
  if (price === null || price === undefined || price === "") return null;
  const n = typeof price === "number" ? price : Number(price);
  return Number.isFinite(n) ? n : null;
}

function slugifyVendorName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

function buildPayload(
  fields: {
    name: string;
    slug: string;
    description: string;
    websiteUrl: string;
    termsMarkdown: string;
    isActive: boolean;
    isClientOwned: boolean;
    visibleTenantIds: string[];
  },
  logoUploadId: string | null,
  packages: EditablePackage[],
  questions: EditableQuestion[],
  contactEmails: EditableContactEmail[],
  defaultDeliveryDaysInput: string,
  slaDaysPerIterationInput: string,
  capabilities: VendorCapability[],
): AdminVendorWritePayload {
  const addresses = contactEmails
    .map((row) => ({ ...row, email: row.email.trim() }))
    .filter((row) => row.email.length > 0);
  const primary = addresses.find((row) => row.isPrimary) ?? addresses[0];

  return {
    name: fields.name,
    slug: fields.slug.trim(),
    description: fields.description || null,
    logoUploadId: logoUploadId,
    websiteUrl: fields.websiteUrl || null,
    defaultDeliveryDays: (() => {
      const trimmed = defaultDeliveryDaysInput.trim();
      if (!trimmed) return null;
      const parsed = Number(trimmed);
      return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    })(),
    slaDaysPerIteration: (() => {
      const trimmed = slaDaysPerIterationInput.trim();
      if (!trimmed) return 5;
      const parsed = Number(trimmed);
      return Number.isInteger(parsed) && parsed > 0 ? parsed : 5;
    })(),
    // The legacy single column stays in sync with the primary approved address
    // until nothing reads it (expand/contract).
    contactEmail: resolveLegacyContactEmail(addresses, primary?.uid),
    contactEmails: addresses.map((row) => ({
      ...(row.id ? { id: row.id } : {}),
      email: row.email,
      isPrimary: row.uid === primary?.uid,
      receivesRevisionRequested: row.receivesRevisionRequested,
      receivesCommentUpdates: row.receivesCommentUpdates,
    })),
    termsMarkdown: fields.termsMarkdown || null,
    isActive: fields.isActive,
    isClientOwned: fields.isClientOwned,
    visibleTenantIds: fields.isClientOwned ? fields.visibleTenantIds : undefined,
    capabilities,
    packages: packages.map(({ uid: _uid, ...p }, i) => ({
      ...p,
      sortOrder: i,
      price: normalizePackagePrice(p.price),
    })),
    questions: questions.map(({ uid: _uid, optionUids: _optionUids, ...q }, i) => ({
      ...q,
      sortOrder: i,
      options: q.type === "SELECT" ? (q.options ?? []).filter(Boolean) : undefined,
      allowMultiple: q.type === "SELECT" ? (q.allowMultiple ?? true) : undefined,
    })),
  };
}

export function AdminVendorForm({ onClose, vendor, pendingContext }: AdminVendorFormProps) {
  const { t } = useTranslation();
  const isEdit = !!vendor;
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const tenants = useAuth((s) => s.tenants);
  const isSuperTenant = tenants.some((tenant) => tenant.type === "SUPER_TENANT");
  const canConnectPartnerOrg = isSuperTenant;
  const canUnlinkPartnerOrg = isSuperTenant;
  const queryClient = useQueryClient();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [createPartnerOrg, setCreatePartnerOrg] = useState(true);
  const [linkedPartner, setLinkedPartner] = useState<{ id: string; name: string } | null>(
    vendor?.partnerTenant
      ? { id: vendor.partnerTenant.id, name: vendor.partnerTenant.name }
      : vendor?.partnerTenantId
        ? { id: vendor.partnerTenantId, name: vendor.name }
        : null,
  );

  const initialIsClientOwned = vendor?.ownership === "CLIENT_OWNED";

  const initialVisibleTenantIds =
    vendor?.tenantVisibility?.map((row) => row.tenantId) ??
    (pendingContext && initialIsClientOwned ? [pendingContext.tenantId] : []);

  const [fields, setFields] = useState({
    name: vendor?.name ?? pendingContext?.rawLabel ?? "",
    slug: vendor?.slug ?? (pendingContext ? slugifyVendorName(pendingContext.rawLabel) : ""),
    description: vendor?.description ?? "",
    websiteUrl: vendor?.websiteUrl ?? "",
    termsMarkdown: vendor?.termsMarkdown ?? "",
    isActive: vendor?.isActive ?? true,
    isClientOwned: initialIsClientOwned,
    visibleTenantIds: initialVisibleTenantIds,
  });

  const [defaultDeliveryDays, setDefaultDeliveryDays] = useState(
    vendor?.defaultDeliveryDays != null ? String(vendor.defaultDeliveryDays) : "",
  );
  const [slaDaysPerIteration, setSlaDaysPerIteration] = useState(
    vendor?.slaDaysPerIteration != null ? String(vendor.slaDaysPerIteration) : "3",
  );
  const [capabilities, setCapabilities] = useState<VendorCapability[]>(() =>
    uniqueVendorCapabilities(vendor?.capabilities),
  );

  const [contactEmails, setContactEmails] = useState<EditableContactEmail[]>(() => {
    if (!vendor && pendingContext?.contactEmail) {
      return [
        {
          uid: crypto.randomUUID(),
          email: pendingContext.contactEmail,
          approval: "PENDING",
          isPrimary: true,
          receivesRevisionRequested: true,
          receivesCommentUpdates: true,
          persistedEmail: pendingContext.contactEmail,
        },
      ];
    }
    return toEditableContactEmails(vendor);
  });
  const [contactEmailErrors, setContactEmailErrors] = useState<VendorContactEmailErrors>({
    rows: {},
  });

  /** Upload ID referencing the uploads table — persisted to vendor on save. */
  const [logoUploadId, setLogoUploadId] = useState<string | null>(vendor?.logoUploadId ?? null);
  /** Signed URL from API for an existing vendor logo — display only. */
  const [existingLogoUrl] = useState<string | null>(vendor?.logoUrl ?? null);
  /** Local object URL for the file just picked — for immediate preview. */
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  /** True when the user explicitly removed the logo (clears existingLogoUrl from preview). */
  const [logoRemoved, setLogoRemoved] = useState(false);
  /** 0–100 during PUT to GCS, null when idle. */
  const [logoUploadProgress, setLogoUploadProgress] = useState<number | null>(null);

  // Revoke the object URL when it changes or the sheet unmounts.
  useEffect(() => {
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
  }, [logoPreviewUrl]);

  const displayLogoUrl = logoRemoved ? null : (logoPreviewUrl ?? existingLogoUrl);

  const [packages, setPackages] = useState<EditablePackage[]>(
    vendor?.packages.map((p, i) =>
      toEditablePackage({
        id: p.id,
        name: p.name,
        description: p.description,
        price: p.price != null ? Number(p.price) : null,
        currency: p.currency,
        // Legacy free-text cadences predate the controlled vocabulary; drop the
        // out-of-vocab value to null so the (now strict) enum doesn't 400 an
        // otherwise-unrelated edit. The admin re-picks from the dropdown.
        cadence: isPackageCadence(p.cadence) ? p.cadence : null,
        isPopular: p.isPopular,
        sortOrder: i,
        isActive: p.isActive,
      }),
    ) ?? [],
  );

  const [questions, setQuestions] = useState<EditableQuestion[]>(
    vendor?.questions.map((q, i) =>
      toEditableQuestion({
        id: q.id,
        prompt: q.prompt,
        type: q.type,
        options: q.options,
        allowMultiple: q.allowMultiple,
        isRequired: q.isRequired,
        sortOrder: i,
      }),
    ) ?? [],
  );

  const createMutation = useCreateAdminVendor();
  const replaceMutation = useReplaceAdminVendor();
  const approvalMutation = useSetVendorContactEmailApproval();
  const linkPendingMutation = useLinkPendingVendor();
  const enablePartnerLogin = useEnablePartnerLogin();
  const unlinkPartnerLogin = useUnlinkPartnerLogin();
  const isPending =
    createMutation.isPending ||
    replaceMutation.isPending ||
    approvalMutation.isPending ||
    linkPendingMutation.isPending ||
    enablePartnerLogin.isPending ||
    unlinkPartnerLogin.isPending;

  function handleToggleApproval(row: EditableContactEmail, approve: boolean) {
    if (!vendor?.id || !row.id) return;
    approvalMutation.mutate(
      { vendorId: vendor.id, emailId: row.id, approve },
      {
        onSuccess: () =>
          patchContactEmail(row.uid, { approval: approve ? "APPROVED" : "PENDING" }),
      },
    );
  }
  const isLogoUploading = logoUploadProgress !== null;

  const [errors, setErrors] = useState<VendorFieldErrors>({});
  const [touched, setTouched] = useState<Partial<Record<VendorBasicsField, boolean>>>({});
  // Sections that hold validatable fields are controlled so we can re-open them
  // when a collapsed section hides a validation error on submit.
  const [basicsOpen, setBasicsOpen] = useState(true);
  const [agreementOpen, setAgreementOpen] = useState(true);
  const [packagesOpen, setPackagesOpen] = useState(true);
  const [questionsOpen, setQuestionsOpen] = useState(true);
  const [partnerOpen, setPartnerOpen] = useState(true);
  // Tracks a save attempt so the "needs a package" error only shows after submit.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Nav rail: track which section is in view and jump to sections on click.
  const rootRef = useRef<HTMLDivElement>(null);
  const initialDirtySnapshotRef = useRef<VendorFormDirtySnapshot | null>(null);
  const [activeSection, setActiveSection] = useState<SectionKey>("basics");

  const SECTION_OPENERS: Record<SectionKey, (open: boolean) => void> = {
    basics: setBasicsOpen,
    partner: setPartnerOpen,
    agreement: setAgreementOpen,
    packages: setPackagesOpen,
    questions: setQuestionsOpen,
  };

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const scrollRoot = findVerticalScrollParent(root);
    const idToKey = Object.fromEntries(
      Object.entries(SECTION_ID).map(([key, id]) => [id, key]),
    ) as Record<string, SectionKey>;
    const observer = new IntersectionObserver(
      (entries) => {
        // Of the sections crossing the top band, the lowest one (largest top)
        // is the one we've most recently scrolled into.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.boundingClientRect.top - a.boundingClientRect.top)[0];
        const key = visible ? idToKey[visible.target.id] : undefined;
        if (key) setActiveSection(key);
      },
      { root: scrollRoot, rootMargin: "0px 0px -65% 0px", threshold: 0 },
    );
    Object.values(SECTION_ID).forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  function goToSection(key: SectionKey) {
    SECTION_OPENERS[key](true);
    setActiveSection(key);
    requestAnimationFrame(() => {
      const section = document.getElementById(SECTION_ID[key]);
      if (section) scrollSectionToStart(section);
    });
  }

  const packagesError = submitAttempted && packages.length < VENDOR_FORM_LIMITS.packageCountMin;

  // Per-section error flags for the nav rail — only after a save attempt, and
  // mirroring how handleSubmit maps invalid fields back to their sections.
  const hasContactEmailError =
    Boolean(contactEmailErrors.list) || Object.keys(contactEmailErrors.rows).length > 0;

  const sectionErrors: Record<SectionKey, boolean> = {
    basics: submitAttempted && Boolean(errors.name || errors.slug),
    partner: false,
    agreement:
      submitAttempted &&
      Boolean(errors.websiteUrl || errors.termsMarkdown || hasContactEmailError),
    packages: packagesError,
    questions: false,
  };

  function setField<K extends keyof typeof fields>(key: K, value: (typeof fields)[K]) {
    setFields((prev) => ({ ...prev, [key]: value }));
    // Keep errors current on every keystroke; `fieldError` decides when to show them.
    setErrors(validateVendorBasics({ ...fields, [key]: value }));
  }

  function handleFieldBlur(field: VendorBasicsField) {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors(validateVendorBasics(fields));
  }

  /**
   * Show a field's error after it's been touched or submit attempted. The
   * optional format fields (website, email) validate eagerly — as soon as they
   * hold a non-empty, malformed value — so typos surface without leaving the field.
   */
  function fieldError(field: VendorBasicsField): string | undefined {
    const isEagerFormatField = field === "websiteUrl";
    const hasValue = fields[field].trim().length > 0;
    const shouldShow = submitAttempted || touched[field] || (isEagerFormatField && hasValue);
    return shouldShow ? errors[field] : undefined;
  }

  function patchContactEmail(uid: string, patch: Partial<EditableContactEmail>) {
    setContactEmails((prev) => {
      const next = prev.map((row) => (row.uid === uid ? { ...row, ...patch } : row));
      setContactEmailErrors(validateVendorContactEmails(next));
      return next;
    });
  }

  function addContactEmail() {
    setContactEmails((prev) =>
      prev.length >= CONTACT_EMAIL_COUNT_MAX ? prev : [...prev, newContactEmailRow()],
    );
  }

  function removeContactEmail(uid: string) {
    setContactEmails((prev) => {
      const next = prev.filter((row) => row.uid !== uid);
      const result = next.length > 0 ? next : [newContactEmailRow()];
      setContactEmailErrors(validateVendorContactEmails(result));
      return result;
    });
  }

  /** Exactly one primary, mirroring the single-primary rule the API enforces. */
  function setPrimaryContactEmail(uid: string) {
    setContactEmails((prev) => prev.map((row) => ({ ...row, isPrimary: row.uid === uid })));
  }

  const termsDirty = fields.termsMarkdown !== (vendor?.termsMarkdown ?? "");

  const currentDirtySnapshot = captureVendorFormDirtySnapshot({
    name: fields.name,
    slug: fields.slug,
    description: fields.description,
    capabilities,
    logoUploadId,
    logoRemoved,
    hasLocalLogoPreview: Boolean(logoPreviewUrl),
    websiteUrl: fields.websiteUrl,
    defaultDeliveryDays,
    slaDaysPerIteration,
    termsMarkdown: fields.termsMarkdown,
    isActive: fields.isActive,
    isClientOwned: fields.isClientOwned,
    visibleTenantIds: fields.visibleTenantIds,
    contactEmails,
    packages,
    questions,
  });
  if (isEdit && initialDirtySnapshotRef.current === null) {
    initialDirtySnapshotRef.current = currentDirtySnapshot;
  }
  const sectionDirty =
    isEdit && initialDirtySnapshotRef.current
      ? getDirtyVendorFormSections(initialDirtySnapshotRef.current, currentDirtySnapshot)
      : CLEAN_SECTION_DIRTY;

  function patchPackage(idx: number, patch: Partial<EditablePackage>) {
    setPackages((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  }

  function patchQuestion(idx: number, patch: Partial<EditableQuestion>) {
    setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  }

  // Only one package can be Popular; clicking the current one clears it.
  function togglePopular(idx: number) {
    setPackages((prev) =>
      prev.map((p, i) => ({ ...p, isPopular: i === idx ? !p.isPopular : false })),
    );
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handlePackageDragEnd = makeUidDragEndHandler(setPackages);
  const handleQuestionDragEnd = makeUidDragEndHandler(setQuestions);

  async function handleLogoFileChange(file: File | undefined) {
    if (!file || !activeTenantId) return;

    if (!isAllowedVendorLogoType(file.type)) {
      toast.error(t("marketplace.admin.form.logoInvalidType"));
      return;
    }

    const maxBytes = getVendorLogoMaxBytes(file.type);
    if (maxBytes != null && file.size > maxBytes) {
      toast.error(t("marketplace.admin.form.logoTooLarge"));
      return;
    }

    // Show local preview immediately — no waiting for the upload to finish.
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoPreviewUrl(URL.createObjectURL(file));
    setLogoRemoved(false);
    setLogoUploadProgress(0);

    try {
      const presign = await presignVendorLogoUpload({
        tenantId: activeTenantId,
        contentType: file.type,
        sizeBytes: file.size,
        originalName: file.name,
      });

      if (presign.requiresMemoryStage || presign.requiresServerStage) {
        await stageMemoryPreflightUpload({
          tenantId: activeTenantId,
          uploadId: presign.uploadId,
          contentType: file.type,
          file,
        });
        setLogoUploadProgress(100);
      } else {
        await putToSignedUrlWithProgress(presign.uploadUrl, file, presign.requiredHeaders, {
          onProgress: setLogoUploadProgress,
        });
      }

      const confirmed = await confirmVendorLogoUpload({
        tenantId: activeTenantId,
        uploadId: presign.uploadId,
      });

      setLogoUploadId(confirmed.uploadId);
      toast.success(t("marketplace.admin.form.logoUploaded"));
    } catch {
      toast.error(t("marketplace.admin.form.logoUploadFailed"));
      // Revert preview on failure.
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
      setLogoPreviewUrl(null);
    } finally {
      setLogoUploadProgress(null);
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
    }
  }

  function handleLogoRemove() {
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoPreviewUrl(null);
    setLogoUploadId(null);
    setLogoRemoved(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    setSubmitAttempted(true);
    const nextErrors = validateVendorBasics(fields);
    const nextEmailErrors = validateVendorContactEmails(contactEmails);
    const hasEmailError =
      Boolean(nextEmailErrors.list) || Object.keys(nextEmailErrors.rows).length > 0;
    const hasPackagesError = packages.length < VENDOR_FORM_LIMITS.packageCountMin;

    if (Object.keys(nextErrors).length > 0 || hasEmailError || hasPackagesError) {
      setErrors(nextErrors);
      setContactEmailErrors(nextEmailErrors);
      setTouched({
        name: true,
        slug: true,
        websiteUrl: true,
        termsMarkdown: true,
      });
      // Re-open any collapsed section that hides an invalid field so the error is visible.
      if (nextErrors.name || nextErrors.slug) setBasicsOpen(true);
      if (nextErrors.websiteUrl || nextErrors.termsMarkdown || hasEmailError) {
        setAgreementOpen(true);
      }
      if (hasPackagesError) setPackagesOpen(true);

      const firstInvalid = BASICS_FIELD_ORDER.find((field) => nextErrors[field]);
      const firstInvalidEmailUid = contactEmails.find((row) => nextEmailErrors.rows[row.uid])?.uid;
      // Focus after the re-render so a just-expanded control can receive focus and scroll into view.
      requestAnimationFrame(() => {
        const targetId = firstInvalid
          ? BASICS_FIELD_INPUT_ID[firstInvalid]
          : firstInvalidEmailUid
            ? `vf-email-${firstInvalidEmailUid}`
            : hasPackagesError
              ? "vf-add-package"
              : undefined;
        if (targetId) document.getElementById(targetId)?.focus();
      });

      toast.error(t("marketplace.admin.form.errors.fixErrors"));
      return;
    }

    // Validate packages/SELECT-question contents the way the API will, so a
    // stripped-empty or duplicate option doesn't bounce back as an opaque 400.
    const subEntityError = validateVendorSubEntities(packages, questions);
    if (subEntityError) {
      SECTION_OPENERS[subEntityError.section](true);
      toast.error(
        t(`marketplace.admin.form.errors.${subEntityError.messageKey}`, subEntityError.values),
      );
      return;
    }

    const payload = {
      ...buildPayload(
        fields,
        logoUploadId,
        packages,
        questions,
        contactEmails,
        defaultDeliveryDays,
        slaDaysPerIteration,
        capabilities,
      ),
      ...(isSuperTenant && !isEdit ? { createPartnerOrg } : {}),
    };
    try {
      if (isEdit) {
        await replaceMutation.mutateAsync({ vendorId: vendor.id, payload });
      } else {
        const created = await createMutation.mutateAsync(payload);
        if (pendingContext) {
          try {
            await linkPendingMutation.mutateAsync({
              pendingId: pendingContext.id,
              vendorId: created.id,
            });
          } catch {
            toast.error(
              t("marketplace.admin.form.errors.linkPendingFailed", { name: created.name }),
            );
            return;
          }
        }
      }
    } catch {
      return;
    }
    onClose();
  }

  return (
    <div
      ref={rootRef}
      className="lg:grid lg:grid-cols-[14rem_minmax(0,1fr)] lg:items-start lg:gap-10"
    >
      <FormNavRail
        logoUrl={displayLogoUrl}
        name={fields.name}
        slug={fields.slug}
        isActive={fields.isActive}
        isEdit={isEdit}
        activeSection={activeSection}
        packagesCount={packages.length}
        questionsCount={questions.length}
        sectionErrors={sectionErrors}
        sectionDirty={sectionDirty}
        showPartnerSection={isSuperTenant}
        onNavigate={goToSection}
      />

      <div className="flex min-w-0 flex-col gap-6">
        <form
          id="vendor-form"
          noValidate
          onSubmit={(e) => void handleSubmit(e)}
          className="flex flex-col gap-6"
        >
          {/* ─── Basics ─── */}
          <CollapsibleSection
            id={SECTION_ID.basics}
            step={1}
            title={t("marketplace.admin.sections.basics")}
            hint={t("marketplace.admin.sections.basicsHint")}
            open={basicsOpen}
            onOpenChange={setBasicsOpen}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vf-name" className="flex items-center gap-0.5">
                {t("marketplace.admin.form.name")}
                <span className="text-sm text-destructive" aria-hidden>
                  *
                </span>
              </Label>
              <Input
                id="vf-name"
                required
                maxLength={VENDOR_TEXT_LIMITS.nameMax}
                value={fields.name}
                onChange={(e) => setField("name", e.target.value)}
                onBlur={() => handleFieldBlur("name")}
                aria-invalid={fieldError("name") ? true : undefined}
                aria-describedby={fieldError("name") ? "vf-name-error" : undefined}
              />
              {fieldError("name") && (
                <p id="vf-name-error" role="alert" className="text-xs text-destructive">
                  {t(fieldError("name")!)}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vf-slug" className="flex items-center gap-0.5">
                {t("marketplace.admin.form.slug")}
                <span className="text-sm text-destructive" aria-hidden>
                  *
                </span>
              </Label>
              <Input
                id="vf-slug"
                required
                maxLength={VENDOR_TEXT_LIMITS.nameMax}
                value={fields.slug}
                onChange={(e) => setField("slug", e.target.value.toLowerCase())}
                onBlur={() => handleFieldBlur("slug")}
                aria-invalid={fieldError("slug") ? true : undefined}
                aria-describedby={
                  fieldError("slug") ? "vf-slug-hint vf-slug-error" : "vf-slug-hint"
                }
              />
              <p id="vf-slug-hint" className="text-xs text-muted-foreground">
                {t("marketplace.admin.form.slugHint", "Must be unique across all partners")}
              </p>
              {fieldError("slug") && (
                <p id="vf-slug-error" role="alert" className="text-xs text-destructive">
                  {t(fieldError("slug")!)}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="vf-desc">{t("marketplace.admin.form.description")}</Label>
                {fields.description.length >= VENDOR_TEXT_LIMITS.descriptionMax * 0.9 && (
                  <span className="shrink-0 text-xs tabular-nums text-status-warning">
                    {fields.description.length}/{VENDOR_TEXT_LIMITS.descriptionMax}
                  </span>
                )}
              </div>
              <Textarea
                id="vf-desc"
                rows={2}
                maxLength={VENDOR_TEXT_LIMITS.descriptionMax}
                value={fields.description}
                onChange={(e) => setField("description", e.target.value)}
              />
            </div>

            <VendorCapabilitiesField
              value={capabilities}
              name={fields.name}
              description={fields.description}
              packages={packages}
              onChange={setCapabilities}
            />

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vf-logo">{t("marketplace.admin.form.logo")}</Label>
              <div className="flex items-start gap-3">
                {/* Thumbnail preview */}
                <div className="relative shrink-0">
                  {displayLogoUrl ? (
                    <MediaPreview
                      src={displayLogoUrl}
                      thumbnailSrc={displayLogoUrl}
                      type="image"
                      alt={fields.name || "Logo"}
                      title={fields.name || "Logo"}
                      className="h-14 w-14 rounded-lg border border-border/60 bg-background object-contain"
                    />
                  ) : (
                    <div
                      aria-hidden="true"
                      className="flex h-14 w-14 items-center justify-center rounded-lg border border-border/60 bg-muted text-sm font-semibold text-muted-foreground"
                    >
                      {fields.name ? getVendorInitials(fields.name) : <ImageIcon className="h-5 w-5 opacity-40" />}
                    </div>
                  )}
                  {/* Upload progress ring overlay */}
                  {isLogoUploading && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/80">
                      <span className="text-[10px] font-semibold tabular-nums text-foreground">
                        {logoUploadProgress ?? 0}%
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-1 flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      ref={logoInputRef}
                      id="vf-logo"
                      type="file"
                      accept={VENDOR_LOGO_ACCEPT}
                      className="sr-only"
                      disabled={isLogoUploading || isPending || !activeTenantId}
                      onChange={(e) => void handleLogoFileChange(e.target.files?.[0])}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isLogoUploading || isPending || !activeTenantId}
                      onClick={() => logoInputRef.current?.click()}
                    >
                      <Upload className="mr-1.5 h-3.5 w-3.5" />
                      {isLogoUploading
                        ? t("marketplace.admin.form.logoUploading")
                        : t("marketplace.admin.form.logoUpload")}
                    </Button>
                    {(displayLogoUrl || logoUploadId) && !isLogoUploading && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={isPending}
                        onClick={handleLogoRemove}
                      >
                        <X className="mr-1.5 h-3.5 w-3.5" />
                        {t("marketplace.admin.form.logoRemove")}
                      </Button>
                    )}
                  </div>

                  {/* Progress bar */}
                  {isLogoUploading && (
                    <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className={cn(
                          "h-full rounded-full bg-primary transition-all duration-200",
                        )}
                        style={{ width: `${logoUploadProgress ?? 0}%` }}
                      />
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">
                    {t("marketplace.admin.form.logoHint")}
                  </p>
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {isSuperTenant ? (
            <PartnerOrgSection
              isEdit={isEdit}
              createPartnerOrg={createPartnerOrg}
              onCreatePartnerOrgChange={setCreatePartnerOrg}
              linkedPartner={linkedPartner}
              connecting={enablePartnerLogin.isPending}
              unlinking={unlinkPartnerLogin.isPending}
              canConnect={canConnectPartnerOrg}
              canUnlink={canUnlinkPartnerOrg}
              onConnect={() => {
                if (!vendor) return;
                enablePartnerLogin.mutate(vendor.id, {
                  onSuccess: async (data) => {
                    try {
                      const refreshed = await queryClient.fetchQuery({
                        ...adminVendorQueryOptions(vendor.id),
                        staleTime: 0,
                      });
                      setLinkedPartner({
                        id: refreshed.partnerTenant?.id ?? data.tenantId,
                        name: refreshed.partnerTenant?.name ?? vendor.name,
                      });
                    } catch {
                      setLinkedPartner({
                        id: data.tenantId,
                        name: vendor.name,
                      });
                    }
                  },
                });
              }}
              onUnlink={() => {
                if (!vendor) return;
                if (
                  !window.confirm(
                    t(
                      "marketplace.admin.partner.unlinkConfirm",
                      "Unlink this Partner org? The organization and people with Access stay; they just will not be tied to this partner.",
                    ),
                  )
                ) {
                  return;
                }
                unlinkPartnerLogin.mutate(vendor.id, {
                  onSuccess: () => setLinkedPartner(null),
                });
              }}
              open={partnerOpen}
              onOpenChange={setPartnerOpen}
            />
          ) : null}

          {/* ─── Agreement & contact ─── */}
          <CollapsibleSection
            id={SECTION_ID.agreement}
            step={isSuperTenant ? 3 : 2}
            title={t("marketplace.admin.sections.agreement")}
            hint={t("marketplace.admin.sections.agreementHint")}
            open={agreementOpen}
            onOpenChange={setAgreementOpen}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vf-web">{t("marketplace.admin.form.websiteUrl")}</Label>
              <Input
                id="vf-web"
                type="url"
                inputMode="url"
                maxLength={VENDOR_TEXT_LIMITS.websiteUrlMax}
                placeholder="https://example.com"
                value={fields.websiteUrl}
                onChange={(e) => setField("websiteUrl", e.target.value)}
                onBlur={() => handleFieldBlur("websiteUrl")}
                aria-invalid={fieldError("websiteUrl") ? true : undefined}
                aria-describedby={fieldError("websiteUrl") ? "vf-web-error" : undefined}
              />
              {fieldError("websiteUrl") && (
                <p id="vf-web-error" role="alert" className="text-xs text-destructive">
                  {t(fieldError("websiteUrl")!)}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vf-delivery-days">
                {t("marketplace.admin.form.defaultDeliveryDays", "Default delivery lead time (days)")}
              </Label>
              <Input
                id="vf-delivery-days"
                type="number"
                inputMode="numeric"
                min={1}
                max={365}
                placeholder="14"
                value={defaultDeliveryDays}
                onChange={(e) => setDefaultDeliveryDays(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  "marketplace.admin.form.defaultDeliveryDaysHint",
                  "Prefills the expected delivery date when this partner is assigned during production sheet import.",
                )}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="vf-sla-days">
                {t("marketplace.admin.form.slaDaysPerIteration", "SLA per iteration (days)")}
              </Label>
              <Input
                id="vf-sla-days"
                type="number"
                inputMode="numeric"
                min={1}
                max={365}
                placeholder="5"
                value={slaDaysPerIteration}
                onChange={(e) => setSlaDaysPerIteration(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  "marketplace.admin.form.slaDaysPerIterationHint",
                  "Calendar days added to each script's expected delivery when an order is sent or a revision is requested.",
                )}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-0.5">
                <Label className="flex items-center gap-0.5">
                  {t("marketplace.admin.form.contactEmails")}
                  <span className="text-sm text-destructive" aria-hidden>
                    *
                  </span>
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t("marketplace.admin.form.contactEmailsHint")}
                </p>
              </div>

              {contactEmails.map((row, index) => {
                const rowError = contactEmailErrors.rows[row.uid];
                const inputId = `vf-email-${row.uid}`;
                const isApproved = row.approval === "APPROVED";
                const isUnsaved = !isContactEmailPersisted(row);
                return (
                  <div key={row.uid} className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Input
                        id={inputId}
                        type="email"
                        inputMode="email"
                        maxLength={VENDOR_TEXT_LIMITS.contactEmailMax}
                        placeholder={t(
                          "marketplace.admin.form.contactEmailPlaceholder",
                          "partner@example.com",
                        )}
                        value={row.email}
                        onChange={(e) => patchContactEmail(row.uid, { email: e.target.value })}
                        aria-invalid={rowError ? true : undefined}
                        aria-describedby={rowError ? `${inputId}-error` : undefined}
                        aria-label={t("marketplace.admin.form.contactEmailRowLabel", {
                          index: index + 1,
                        })}
                      />
                      <Badge variant={isApproved ? "secondary" : "outline"}>
                        {t(
                          isApproved
                            ? "marketplace.admin.form.emailApproved"
                            : "marketplace.admin.form.emailPending",
                        )}
                      </Badge>
                      {isSuperTenant && (
                        <Button
                          type="button"
                          variant={isApproved ? "ghost" : "outline"}
                          size="sm"
                          className="shrink-0"
                          disabled={isUnsaved || approvalMutation.isPending}
                          title={
                            isUnsaved
                              ? t("marketplace.admin.form.approveNeedsSave")
                              : undefined
                          }
                          onClick={() => handleToggleApproval(row, !isApproved)}
                        >
                          {t(
                            isApproved
                              ? "marketplace.admin.form.revokeEmail"
                              : "marketplace.admin.form.approveEmail",
                          )}
                        </Button>
                      )}
                      <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                        <input
                          type="radio"
                          name="vf-primary-email"
                          checked={row.isPrimary}
                          onChange={() => setPrimaryContactEmail(row.uid)}
                          aria-label={t("marketplace.admin.form.setPrimaryEmail", {
                            email: row.email || t("marketplace.admin.form.contactEmails"),
                          })}
                        />
                        {t("marketplace.admin.form.primaryEmail")}
                      </label>
                      <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                        <Switch
                          checked={row.receivesRevisionRequested}
                          onCheckedChange={(receivesRevisionRequested) =>
                            patchContactEmail(row.uid, { receivesRevisionRequested })
                          }
                          aria-label={t("marketplace.admin.form.revisionEmailsToggleAria", {
                            email: row.email || t("marketplace.admin.form.contactEmails"),
                            defaultValue: "Send {{email}} back-to-revision alerts",
                          })}
                        />
                        {t("marketplace.admin.form.revisionEmails", "Back to revision")}
                      </label>
                      <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                        <Switch
                          checked={row.receivesCommentUpdates}
                          onCheckedChange={(receivesCommentUpdates) =>
                            patchContactEmail(row.uid, { receivesCommentUpdates })
                          }
                          aria-label={t("marketplace.admin.form.commentUpdatesEmailsToggleAria", {
                            email: row.email || t("marketplace.admin.form.contactEmails"),
                            defaultValue: "Send {{email}} script comment update alerts",
                          })}
                        />
                        {t("marketplace.admin.form.commentUpdatesEmails", "Comment updates")}
                      </label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeContactEmail(row.uid)}
                        aria-label={t("marketplace.admin.form.removeEmail", {
                          email: row.email,
                        })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {rowError && (
                      <p id={`${inputId}-error`} role="alert" className="text-xs text-destructive">
                        {t(rowError)}
                      </p>
                    )}
                  </div>
                );
              })}

              {contactEmailErrors.list && (
                <p role="alert" className="text-xs text-destructive">
                  {t(contactEmailErrors.list)}
                </p>
              )}

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                disabled={contactEmails.length >= CONTACT_EMAIL_COUNT_MAX}
                onClick={addContactEmail}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t("marketplace.admin.form.addContactEmail")}
              </Button>
            </div>

            <TermsField
              value={fields.termsMarkdown}
              onChange={(v) => setField("termsMarkdown", v)}
              onBlur={() => handleFieldBlur("termsMarkdown")}
              currentVersion={isEdit ? vendor?.termsVersion : undefined}
              isDirty={termsDirty}
              disabled={isPending}
              error={fieldError("termsMarkdown") ? t(fieldError("termsMarkdown")!) : undefined}
            />

            <label className="flex cursor-pointer items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
              <span className="flex flex-col">
                <span className="text-sm font-medium">
                  {t("marketplace.admin.form.isActive")}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t("marketplace.admin.form.isActiveHint", {
                    defaultValue: "Visible to tenants in the catalog.",
                  })}
                </span>
              </span>
              <Switch
                id="vf-active"
                checked={fields.isActive}
                onCheckedChange={(v) => setField("isActive", v)}
              />
            </label>

            <label className="flex cursor-pointer items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
              <span className="flex flex-col">
                <span className="text-sm font-medium">
                  {t("marketplace.admin.form.isClientOwned", "In-house partner (client-owned)")}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t(
                    "marketplace.admin.form.isClientOwnedHint",
                    "Restrict catalog visibility to selected client organizations.",
                  )}
                </span>
              </span>
              <Switch
                id="vf-client-owned"
                checked={fields.isClientOwned}
                onCheckedChange={(checked) =>
                  setFields((prev) => ({
                    ...prev,
                    isClientOwned: checked,
                    visibleTenantIds:
                      checked && prev.visibleTenantIds.length === 0
                        ? pendingContext
                          ? [pendingContext.tenantId]
                          : activeTenantId
                            ? [activeTenantId]
                            : []
                        : prev.visibleTenantIds,
                  }))
                }
              />
            </label>

            {fields.isClientOwned ? (
              <div className="flex flex-col gap-1.5">
                <Label>{t("marketplace.admin.form.visibleTenants")}</Label>
                <SearchableCombobox
                  items={tenants.map((tenant) => ({
                    id: tenant.id,
                    name: tenant.name,
                  }))}
                  getKey={(tenant) => tenant.id}
                  getLabel={(tenant) => tenant.name}
                  value={fields.visibleTenantIds[0] ?? null}
                  placeholder={t("marketplace.admin.form.visibleTenantsPlaceholder")}
                  searchPlaceholder={t("marketplace.admin.form.visibleTenantsSearch")}
                  emptyMessage={t("marketplace.admin.form.visibleTenantsEmpty")}
                  onSelect={(tenant) =>
                    setFields((prev) => ({
                      ...prev,
                      visibleTenantIds: [tenant.id],
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  {t("marketplace.admin.form.visibleTenantsHint")}
                </p>
              </div>
            ) : null}
          </CollapsibleSection>

          {/* ─── Packages ─── */}
          <CollapsibleSection
            id={SECTION_ID.packages}
            step={isSuperTenant ? 4 : 3}
            title={t("marketplace.admin.form.packages")}
            hint={t("marketplace.admin.sections.packagesHint", {
              defaultValue: "Pricing tiers shown on the partner's catalog card.",
            })}
            count={packages.length}
            open={packagesOpen}
            onOpenChange={setPackagesOpen}
            actions={
              <Button
                id="vf-add-package"
                type="button"
                variant="outline"
                size="sm"
                disabled={packages.length >= VENDOR_FORM_LIMITS.packageCountMax}
                onClick={() => setPackages((prev) => [...prev, createEmptyPackage()])}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                {t("marketplace.admin.form.addPackage")}
              </Button>
            }
          >
            {packages.length === 0 ? (
              <div className="flex flex-col gap-2">
                <SectionEmpty
                  icon={<Package className="h-5 w-5 text-muted-foreground/60" />}
                  label={t("marketplace.admin.form.packagesEmpty", {
                    defaultValue: "No packages yet. Add one to show pricing on the card.",
                  })}
                  isError={packagesError}
                />
                {packagesError && (
                  <p role="alert" className="text-xs text-destructive">
                    {t("marketplace.admin.form.errors.packagesMin")}
                  </p>
                )}
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handlePackageDragEnd}
              >
                <SortableContext
                  items={packages.map((p) => p.uid)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col divide-y divide-border/60">
                    {packages.map((pkg, idx) => (
                      <SortableRow key={pkg.uid} id={pkg.uid}>
                        {(dragHandle) => (
                          <PackageEditor
                            pkg={pkg}
                            index={idx}
                            onChange={(patch) => patchPackage(idx, patch)}
                            onRemove={() =>
                              setPackages((prev) => prev.filter((_, i) => i !== idx))
                            }
                            onMarkPopular={() => togglePopular(idx)}
                            dragHandle={dragHandle}
                          />
                        )}
                      </SortableRow>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </CollapsibleSection>

          {/* ─── Questions ─── */}
          <CollapsibleSection
            id={SECTION_ID.questions}
            step={isSuperTenant ? 5 : 4}
            title={t("marketplace.admin.form.questions")}
            hint={t("marketplace.admin.sections.questionsHint", {
              defaultValue:
                "Onboarding questions tenants answer when connecting. Mix short, long, and multiple-choice answers.",
            })}
            count={questions.length}
            open={questionsOpen}
            onOpenChange={setQuestionsOpen}
            actions={
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={questions.length >= VENDOR_FORM_LIMITS.questionCountMax}
                onClick={() => setQuestions((prev) => [...prev, createEmptyQuestion()])}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                {t("marketplace.admin.form.addQuestion")}
              </Button>
            }
          >
            {questions.length === 0 ? (
              <SectionEmpty
                icon={<ListChecks className="h-5 w-5 text-muted-foreground/60" />}
                label={t("marketplace.admin.form.questionsEmpty", {
                  defaultValue: "No onboarding questions yet. Add your first question.",
                })}
              />
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleQuestionDragEnd}
              >
                <SortableContext
                  items={questions.map((q) => q.uid)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col divide-y divide-border/60">
                    {questions.map((q, idx) => (
                      <SortableRow key={q.uid} id={q.uid}>
                        {(dragHandle) => (
                          <QuestionEditor
                            question={q}
                            index={idx}
                            onChange={(patch) => patchQuestion(idx, patch)}
                            onRemove={() =>
                              setQuestions((prev) => prev.filter((_, i) => i !== idx))
                            }
                            dragHandle={dragHandle}
                          />
                        )}
                      </SortableRow>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </CollapsibleSection>
        </form>

        <div className="sticky bottom-0 z-10 flex items-center gap-3 rounded-xl border border-border bg-background/85 px-4 py-3 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/70">
          <p className="hidden text-xs text-muted-foreground sm:block">
            <span className="text-destructive" aria-hidden>
              *
            </span>{" "}
            {t("marketplace.admin.form.requiredHint", { defaultValue: "Required fields" })}
          </p>
          <div className="ml-auto flex items-center gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              {t("marketplace.admin.form.cancel")}
            </Button>
            <Button type="submit" form="vendor-form" disabled={isPending}>
              {isPending
                ? isEdit
                  ? t("marketplace.admin.form.saving")
                  : t("marketplace.admin.form.creating")
                : isEdit
                  ? t("marketplace.admin.form.save")
                  : t("marketplace.admin.form.create")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

